import { motion, Variants } from 'framer-motion';
import { isEmpty } from 'lodash';
import { Container, Package } from 'lucide-react';
import { useState } from 'react';

import { ShipmentCargo } from '@/lib/models/shipment/types';
import { tv } from '@/utils/styles';

export interface ContainerStackProps {
  cargo?: ShipmentCargo | ShipmentCargo[];
  isOpen?: boolean;
  className?: string;
}

const ContainerStack = ({ cargo, isOpen = false, className }: ContainerStackProps) => {
  // State to track if cargo items are fanned out (for touch devices)
  const [isFanned, setIsFanned] = useState(isOpen);

  const handleClick = (value: boolean) => {
    // Only trigger on touch devices
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      setIsFanned(value);
    }
  };

  if (isEmpty(cargo)) return null;

  const cargoArray: ShipmentCargo[] = [];
  if (Array.isArray(cargo)) {
    cargoArray.push(...cargo);
  } else {
    cargoArray.push(cargo);
  }

  const itemSpacing = 70; // Space between items when fanned out
  const containerPadding = 10;

  const backgroundVariants: Variants = {
    initial: { opacity: 0, scale: 0.8, width: 0, height: 0, x: 0, y: 0, transition: { duration: 0.3 } },
    hover: {
      opacity: 1,
      scale: 1,
      width: Math.min(cargoArray.length, 5) * itemSpacing + containerPadding * 2, // Width based on max items per row + padding
      height: Math.ceil(cargoArray.length / 5) * itemSpacing + containerPadding * 2, // Height based on rows + padding
      x: -containerPadding - containerPadding / 2, // Offset to center the background
      y: -containerPadding - containerPadding / 2, // Offset to center the background
      transition: { duration: 0.3 },
    },
  };

  const getItemVariants = (index: number, xPosition: number, yPosition: number): Variants => ({
    // Stacked position
    initial: {
      x: index * -2,
      y: index * -2,
      opacity: index < 3 ? 1 : 0,
      zIndex: cargoArray.length - index,
      scale: 1,
    },
    // Fanned out position - to the right with wrapping
    hover: {
      x: xPosition,
      y: yPosition,
      opacity: 1,
      zIndex: cargoArray.length - index,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 20,
        delay: index * 0.03, // Staggered animation
      },
    },
  });

  const getLabelVariants = (index: number): Variants => ({
    initial: { opacity: 0, scale: 0.8 },
    hover: {
      opacity: 1,
      scale: 1,
      transition: { delay: index * 0.05 + 0.1 },
    },
  });

  const isContainerizedCargo = cargoArray.some((item) => !!item?.container?.type);
  const { base, itemContainer, icon, label, multiContainer, background } = styles();

  if (cargoArray.length === 1) {
    const item = cargoArray[0];
    return (
      <div className={base({ className })}>
        <div className={itemContainer({ className: 'relative' })}>
          {/* Icon based on container type */}
          {item?.container?.type || isContainerizedCargo ? (
            <Container className={icon()} />
          ) : (
            <Package className={icon()} />
          )}
          {/* Item details label */}
          <span className={label({ isItem: true })}>
            {item.quantity}x {item?.container?.type || 'Item'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={base({ className })}>
      {/* Container that detects hover */}
      <motion.div
        className={multiContainer()}
        whileHover="hover"
        initial="initial"
        animate={isFanned ? 'hover' : 'initial'}
        onClick={() => handleClick(!isFanned)}
      >
        {/* Background container that appears on hover */}
        <motion.div className={background()} variants={backgroundVariants} />
        {cargoArray.map((item, index) => {
          // Calculate fan-out position - only to the right
          // Fan out to the right with a maximum width
          // This ensures items don't go off-screen on most displays
          const maxItems = Math.min(cargoArray.length, 5); // Max 5 items per row
          // Calculate position based on index
          const rowIndex = Math.floor(index / maxItems);
          const colIndex = index % maxItems;
          // Position calculation - fan out to the right and wrap if needed
          const xPosition = colIndex * itemSpacing;
          const yPosition = rowIndex * itemSpacing;
          return (
            <motion.div
              key={index}
              className={itemContainer({ isStacked: true })}
              variants={getItemVariants(index, xPosition, yPosition)}
            >
              {item?.container?.type || isContainerizedCargo ? (
                <Container className={icon()} />
              ) : (
                <Package className={icon()} />
              )}
              <motion.span className={label({ isStacked: true })} variants={getLabelVariants(index)}>
                {item?.quantity} x {item?.container?.size || 'item'}
              </motion.span>
            </motion.div>
          );
        })}
        {/* Total quantity label - only visible when not hovered */}
        <motion.span
          className={label({ isTotal: true })}
          variants={{
            initial: { opacity: 1, scale: 1 },
            hover: { opacity: 0, scale: 0.8, transition: { duration: 0.2 } },
          }}
        >
          {cargoArray.reduce((sum, item) => sum + (item?.quantity || 0), 0)} items
        </motion.span>
      </motion.div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative',
    itemContainer: 'grid size-14 place-items-center rounded-full border border-lightBlue-200 bg-[#ECF3F6] shadow-sm',
    icon: 'size-7 text-lightBlue-600',
    label:
      'absolute -bottom-1 whitespace-nowrap rounded-full border border-lightBlue-300 bg-white px-1 py-0.5 text-center text-[10px] font-semibold leading-none text-lightBlue-700',
    multiContainer: 'group relative grid size-14 cursor-pointer place-items-center',
    background: 'rounded-lg border border-grey-200 bg-white shadow-[0_1px_8px_0_rgba(0,0,0,0.07)]',
  },
  variants: {
    isStacked: {
      true: {
        itemContainer: 'absolute',
        label: '-translate-x-1/2',
      },
    },
    isTotal: {
      true: {
        label: 'z-10 -translate-x-1/2',
      },
    },
    isItem: {
      true: {
        label: 'left-1/2 -translate-x-1/2',
      },
    },
  },
});

export { ContainerStack };
